import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, Award, Building2, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Seo, { ORG_SCHEMA } from "@/components/Seo";

export default function Home() {
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/categories").then(r => setCats(r.data));
    api.get("/products", { params: { featured: true, page_size: 12 } }).then(r => setProducts(r.data.items || []));
  }, []);

  return (
    <div>
      <Seo path="/" schema={ORG_SCHEMA}
           description="Kuzey Yapı Hırdavat — 1916+ orijinal SGS ürünü, bayi sistemi, anlık döviz kuru, hızlı kargo. Çaycuma / Zonguldak'tan tüm Türkiye'ye."/>

      {/* Hero */}
      <section className="bg-ink-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src="https://images.unsplash.com/photo-1597960194599-22929afc25b1?w=1600" alt="" className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/85 to-transparent"></div>
        </div>
        <div className="kz-container relative py-20 md:py-32 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-brand/20 border border-brand/40 text-brand px-3 py-1 mb-6 text-xs font-bold tracking-widest">
              <span className="w-2 h-2 bg-brand"></span>
              KUZEY YAPI HIRDAVAT — ÇAYCUMA / ZONGULDAK
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black leading-[0.95] tracking-tight mb-6">
              SAHADA <span className="text-brand">GÜVEN.</span><br/>
              MAĞAZADA <span className="text-brand">KALİTE.</span>
            </h1>
            <p className="text-lg text-white/70 max-w-xl mb-8">
              Profesyonel ustalar ve inşaat firmaları için <strong className="text-white">1.900+ orijinal ürün</strong>, anlık döviz kuru, hızlı kargo ve özel bayi fiyatlarımızla yanınızdayız.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link data-testid="hero-catalog-btn" to="/katalog" className="kz-btn-primary">
                Kataloğa Göz At <ArrowRight size={18}/>
              </Link>
              <Link data-testid="hero-b2b-btn" to="/bayi-basvuru" className="inline-flex items-center justify-center gap-2 bg-white text-ink-900 font-semibold px-5 py-3 rounded-sm hover:bg-brand hover:text-white transition-colors">
                <Building2 size={18}/> Bayi Ol
              </Link>
            </div>
          </div>
          <div className="hidden lg:flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-brand/30 blur-3xl"></div>
              <img src="https://customer-assets.emergentagent.com/job_kuzey-b2b-hub/artifacts/ngl4j0hv_kuzey%20%20yap%C4%B1%20logo.jpg"
                   alt="Kuzey Yapı Logo"
                   className="relative w-72 h-72 object-cover rounded-sm shadow-2xl border-2 border-white/10"/>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-white border-b border-ink-200">
        <div className="kz-container grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-ink-200">
          {[
            { id: "fast-delivery", icon: Truck, title: "Hızlı Teslimat", text: "Türkiye geneli aynı gün kargo" },
            { id: "original", icon: ShieldCheck, title: "Orijinal Marka", text: "Distribütör garantili ürünler" },
            { id: "wholesale", icon: Award, title: "Toptan Fiyat", text: "Bayilere özel cari ve indirim" },
          ].map((f) => (
            <div key={f.id} className="flex items-center gap-4 p-6">
              <div className="w-12 h-12 bg-ink-100 flex items-center justify-center"><f.icon className="text-brand" size={22}/></div>
              <div>
                <div className="font-display font-bold">{f.title}</div>
                <div className="text-sm text-ink-500">{f.text}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 md:py-24">
        <div className="kz-container">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs font-bold tracking-[0.2em] text-brand mb-2">KATEGORİLER</div>
              <h2 className="font-display text-3xl md:text-4xl font-black">Her İş İçin Doğru Alet</h2>
            </div>
            <Link to="/katalog" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold hover:text-brand">
              Tümünü Gör <ChevronRight size={16}/>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {cats.map((c, idx) => (
              <Link
                key={c.id}
                data-testid={`category-${c.slug}`}
                to={`/katalog?kategori=${c.slug}`}
                className={`relative aspect-square bg-ink-100 border border-ink-200 overflow-hidden group hover:border-ink-900 transition-colors ${idx === 0 ? 'col-span-2 md:col-span-1 lg:col-span-2 lg:row-span-2 aspect-square lg:aspect-auto' : ''}`}
              >
                <img src={c.image_url} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"/>
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent"></div>
                <div className="absolute inset-0 p-4 md:p-6 flex flex-col justify-end text-white">
                  <div className="font-display font-black text-lg md:text-2xl leading-tight">{c.name}</div>
                  <div className="text-xs text-white/70 mt-1 line-clamp-1">{c.description}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-brand">
                    Keşfet <ArrowRight size={12}/>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className="bg-ink-100 py-16 md:py-24">
        <div className="kz-container">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs font-bold tracking-[0.2em] text-brand mb-2">ÖNE ÇIKAN ÜRÜNLER</div>
              <h2 className="font-display text-3xl md:text-4xl font-black">En Çok Tercih Edilenler</h2>
            </div>
            <Link to="/katalog" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold hover:text-brand">
              Tümünü Gör <ChevronRight size={16}/>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {products.slice(0, 8).map((p) => <ProductCard key={p.id} product={p}/>)}
          </div>
        </div>
      </section>

      {/* B2B CTA */}
      <section className="relative py-16 md:py-24 bg-ink-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <img src="https://images.pexels.com/photos/4090524/pexels-photo-4090524.jpeg?w=1600" alt="" className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-r from-ink-900 to-transparent"></div>
        </div>
        <div className="kz-container relative">
          <div className="max-w-2xl">
            <div className="inline-block bg-b2b/20 border border-b2b text-b2b px-3 py-1 mb-4 text-xs font-bold tracking-widest">
              KURUMSAL & B2B
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-black leading-tight mb-4">
              Bayi Olun.<br/><span className="text-brand">Toptan Fiyatlarla</span> Çalışın.
            </h2>
            <p className="text-white/70 mb-6">
              İnşaat firmaları, ustalar ve tedarikçiler için özel fiyatlandırma, cari hesap (ödeme vadesi), toplu sipariş ve adınıza fatura kesim hizmeti.
            </p>
            <ul className="space-y-2 mb-8 text-sm">
              <li className="flex items-center gap-2"><ChevronRight size={16} className="text-brand"/> Adminden tanımlı özel bayi iskontosu</li>
              <li className="flex items-center gap-2"><ChevronRight size={16} className="text-brand"/> Cari hesap ile vadeli ödeme imkânı</li>
              <li className="flex items-center gap-2"><ChevronRight size={16} className="text-brand"/> Hızlı toplu sipariş ve teslimat</li>
              <li className="flex items-center gap-2"><ChevronRight size={16} className="text-brand"/> Adınıza fatura ve sipariş takibi</li>
            </ul>
            <Link data-testid="b2b-cta-btn" to="/bayi-basvuru" className="kz-btn-primary">
              Hemen Başvur <ArrowRight size={18}/>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
